import type { ReportMsg, ReportData, StringKeyAnyValueObject } from '@/types/report';
import { isObject } from 'lodash-es';
import { showUniqueDialog, MsgType } from '@/utils/unique_dialog';
import { login } from '@/utils/auth';
import { redirectToTaihuLogin } from '@/utils/taihu-auth';

const LEVEL_MAP = {
  WHITE_LIST_LOG: {
    level: 1,
    name: '白名单日志',
  },
  COMMON_LOG: {
    level: 2,
    name: '一般日志',
  },
  ERROR_LOG: {
    level: 4,
    name: '错误日志',
  },
  PROMISE_ERROR: {
    level: 8,
    name: 'Promise 错误',
  },
  AJAX_ERROR: {
    level: 16,
    name: 'Ajax 请求异常',
  },
  JS_LOAD_ERROR: {
    level: 32,
    name: 'JS 加载异常',
  },
  PIC_LOAD_ERROR: {
    level: 64,
    name: '图片加载异常',
  },
  MEDIA_ERROR: {
    level: 512,
    name: '音视频资源异常',
  },
  CSS_LOAD_ERROR: {
    level: 128,
    name: 'css 加载异常',
  },
  CONSOLE_ERROR: {
    level: 256,
    name: 'console.error',
  },
  RETCODE_ERROR: {
    level: 1024,
    name: 'retcode 异常',
  },
  AEGIS_REPORT: {
    level: 2048,
    name: 'aegis report',
  },
  PV: {
    level: 4096,
    name: 'PV',
  },
  CUSTOM_EVENT: {
    level: 8192,
    name: '自定义事件',
  },
};
const WHITE_RETS = [8100000, 8100001, 8100002, 8000023, 8000020, 8000024];

export const aegisReport = ({
  msg,
  level = LEVEL_MAP.RETCODE_ERROR.level,
  trace = '',
  refer = '',
}: {
  msg: ReportMsg;
  trace: string;
  level?: number;
  refer?: string;
}) => {
  // (window as any).aegis?.report?.({
  (window as any).aegis?.normalLogPipeline?.({
    msg, // 此时msg是个对象
    level: level || LEVEL_MAP.RETCODE_ERROR.level,
    trace,
    refer,
  });
};

// 新增业务逻辑层的错误自定义上报接口
export const customReport = (
  data: StringKeyAnyValueObject | string,
  url: string,
  reqData: ReportData = {},
  ret: Number = 0,
  extParams: Record<string, unknown> = {},
) => {
  const trace = 'headers' in reqData ? reqData.headers?.['x-trace-id'] : '';
  const standardData = data as unknown as {
    _cuz_report_: number;
    url: string;
    params: Record<string, unknown>;
    error_message: string;
  };
  const formatData = (data: any) => (typeof data === 'object' ? JSON.stringify(data) : data);
  if (
    isObject(standardData) &&
    '_cuz_report_' in standardData &&
    +standardData._cuz_report_ === 1 &&
    standardData.url &&
    standardData.params &&
    standardData.error_message
  ) {
    // 自由模式
    const msg = {
      ...standardData,
      _cuz_report_: undefined,
    };
    aegisReport({ msg: msg as unknown as ReportMsg, trace, ...extParams });
  } else if (isObject(data) && 'result' in data) {
    // 指定结构（data.result）模式
    const { error_code: errorCode = 0, error_message: errorMessage = '' } = (data as StringKeyAnyValueObject).result;
    if (errorMessage && errorCode !== 0 && !WHITE_RETS.includes(errorCode)) {
      const msg = { url, error_message: errorMessage, params: formatData(reqData.data) }; // 将params序列化防止消息过长
      aegisReport({ msg, trace, ...extParams });
    }
  } else if (ret === 50000) {
    const msg = { url, error_message: data as string, params: formatData(reqData.data) };
    aegisReport({ msg, trace, ...extParams });
  } else if (ret === -10000) {
    const msg = { url, error_message: data as string, params: {} };
    aegisReport({ msg, trace, ...extParams });
  }
};

// cache 上报
export const reportCacheStatus = (baseUrl: string, duration: number, cache: String = 'unset') => {
  (window as any)?.aegis?.reportTime?.({
    name: `${baseUrl}?cache=${cache}`,
    duration,
  });
};

export const showTips = (ret: number, message: string, err: any) => {
  // 错误提示
  if (ret === 30000) {
    redirectToTaihuLogin();
    return;
  }
  if (ret === 40000) {
    showUniqueDialog({
      title: 'Permission Denied',
      content: `Please click
        <a
          href="wxwork://message/?username=MI服务助手"
          class="text-brand hover:text-link underline decoration-1"
        >here</a>
        to contact the administrator.`,
      msgType: MsgType.WARNING,
      isMerge: true,
    });
    return;
  }
  if (ret === 60000) {
    showUniqueDialog({
      title: 'Warn',
      content: message,
      msgType: MsgType.WARNING,
      isMerge: false,
    });
    return;
  }
  if (ret === 50000) {
    // 针对一些需求临时处理提示语，后续这里需要从后台做返回分层优化
    let notificationMethod = 'error';
    let description = message !== undefined ? message : err;
    let title = 'Error';

    try {
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/naming-convention,@typescript-eslint/no-unused-vars
      const [_, code, newDesc] = message?.match(/^code:(\d+), msg:(.*)/);
      switch (code?.toString()) {
        case '8000001':
          notificationMethod = 'warning';
          title = 'Warn';
          // description = <>{newDesc} <Tag>code={code}</Tag></>;
          description = newDesc || description;
          break;
        default:
          break;
      }
    } catch (e) {
      console.log(e);
    }
    // @ts-ignore
    showUniqueDialog({
      title,
      content: description,
      msgType: notificationMethod as MsgType, // msgtype 只能是error 或者warning
      isMerge: true,
    });
  }
  if (ret === -1) {
    showUniqueDialog({
      title: 'Error',
      content: message,
      msgType: MsgType.ERROR,
      isMerge: true,
    });
  }
  throw { ret, message, needReport: false };
};
